import axios from 'axios';
import type {
  ShipstationOrder,
  ShipstationOrdersResponse,
  ShipstationTagsResponse,
  FetchNextOrderResponse,
  PrintNextOrderResponse,
  ClaimByLabelResponse,
  ReleaseOrderResponse,
  ReserveOrdersResponse,
  ReleaseBatchResponse,
  SingleSkuSummaryResponse,
  SingleItemPackageWeights,
  ShipBatchResponse,
  ShipLabelStatus,
  ReprintShipLabelResponse,
  SplitOrderResponse,
} from '../schemas/apiResponse.schemas';
import {
  ShipstationOrdersResponseSchema,
  ShipstationOrderSchema,
  ShipstationTagsResponseSchema,
  FetchNextOrderResponseSchema,
  PrintNextOrderResponseSchema,
  ClaimByLabelResponseSchema,
  ReleaseOrderResponseSchema,
  ReserveOrdersResponseSchema,
  ReleaseBatchResponseSchema,
  SingleSkuSummaryResponseSchema,
  SingleItemPackageWeightsSchema,
  ShipBatchResponseSchema,
  ShipLabelStatusSchema,
  ReprintShipLabelResponseSchema,
  SplitOrderResponseSchema,
} from '../schemas/apiResponse.schemas';
import type { OrderReleaseReason } from '@/shared/types/fulfillmentEvents.types';
import { validateResponse } from './validateResponse';

export const fetchOrders = async (): Promise<ShipstationOrdersResponse> => {
  try {
    const response = await axios.get('/api/orders');
    return validateResponse(ShipstationOrdersResponseSchema, response.data, 'fetchOrders');
  } catch (error: unknown) {
    console.error('Error fetching orders:', error);
    return { orders: [] };
  }
};

export const fetchOrderDetails = async (orderId: string | number): Promise<ShipstationOrder> => {
  try {
    const response = await axios.get(`/api/orders/${orderId}`);
    return validateResponse(ShipstationOrderSchema, response.data, 'fetchOrderDetails');
  } catch (error: unknown) {
    console.error('Error fetching order details:', error);
    return { orderId: '', items: [] };
  }
};

export const printNextOrder = async (): Promise<PrintNextOrderResponse> => {
  const response = await axios.post('/api/orders/print/next');
  return validateResponse(PrintNextOrderResponseSchema, response.data, 'printNextOrder');
};

export const claimOrderByLabel = async (
  userTag: number | string,
  orderNumber: string,
): Promise<ClaimByLabelResponse> => {
  const response = await axios.post('/api/orders/claim-by-label', {
    userTag: String(userTag),
    orderNumber,
  });
  return validateResponse(ClaimByLabelResponseSchema, response.data, 'claimOrderByLabel');
};

export const fetchNextOrder = async (userTag: number | string): Promise<FetchNextOrderResponse> => {
  try {
    const response = await axios.post('/api/pick-next-order', {
      userTag: String(userTag),
    });
    return validateResponse(FetchNextOrderResponseSchema, response.data, 'fetchNextOrder');
  } catch (error: unknown) {
    console.error('Error fetching next order:', error);
    throw error;
  }
};

export const releaseOrder = async (
  orderId: string | number,
  userTag: number | string,
  reason: OrderReleaseReason,
): Promise<ReleaseOrderResponse> => {
  try {
    const response = await axios.post(`/api/orders/${orderId}/release`, {
      userTag: String(userTag),
      reason,
    });
    return validateResponse(ReleaseOrderResponseSchema, response.data, 'releaseOrder');
  } catch (error: unknown) {
    console.error('Error releasing order:', error);
    throw error;
  }
};

export const fetchTags = async (options: { forceRefresh?: boolean } = {}): Promise<ShipstationTagsResponse> => {
  try {
    const { forceRefresh = false } = options;
    const params = forceRefresh ? { _: Date.now() } : undefined;
    const response = await axios.get('/api/tags', { params });
    return validateResponse(ShipstationTagsResponseSchema, response.data, 'fetchTags');
  } catch (error: unknown) {
    console.error('Error fetching tags:', error);
    return [];
  }
};

export const fetchOrdersByTagId = async (tagId: number | string): Promise<ShipstationOrdersResponse> => {
  try {
    const response = await axios.get(`/api/orders/tags/${tagId}`);
    return validateResponse(ShipstationOrdersResponseSchema, response.data, 'fetchOrdersByTagId');
  } catch (error: unknown) {
    console.error('Error fetching orders:', error);
    return { orders: [] };
  }
};

export const fetchSingleSkuSummary = async (
  userTag?: number | string,
): Promise<SingleSkuSummaryResponse> => {
  const response = await axios.get('/api/orders/single-sku-summary', {
    params: userTag != null ? { userTag } : undefined,
  });
  return validateResponse(SingleSkuSummaryResponseSchema, response.data, 'fetchSingleSkuSummary');
};

export const fetchSingleItemPackageWeights = async (
  sku: string,
): Promise<SingleItemPackageWeights> => {
  try {
    const response = await axios.get(
      `/api/orders/single-item-package-weights/${encodeURIComponent(sku)}`,
    );
    return validateResponse(
      SingleItemPackageWeightsSchema,
      response.data,
      'fetchSingleItemPackageWeights',
    );
  } catch (error: unknown) {
    console.error('Error fetching single-item package weights:', error);
    return { sku, length: 0, width: 0, height: 0, weightOz: 0 };
  }
};

export const reserveOrders = async (
  orderIds: ReadonlyArray<string | number>,
  userTag: number | string,
): Promise<ReserveOrdersResponse> => {
  const response = await axios.post('/api/orders/reserve', { orderIds, userTag });
  return validateResponse(ReserveOrdersResponseSchema, response.data, 'reserveOrders');
};

export const releaseOrders = async (
  orderIds: ReadonlyArray<string | number>,
  userTag: number | string,
): Promise<ReleaseBatchResponse> => {
  const response = await axios.post('/api/orders/release-batch', { orderIds, userTag });
  return validateResponse(ReleaseBatchResponseSchema, response.data, 'releaseOrders');
};

export const shipBatchOrders = async (payload: {
  orderIds: ReadonlyArray<string | number>;
  sku?: string;
  includeLabelData?: boolean;
  carrierCode?: string;
  serviceCode?: string;
  weight: { value: number; units: 'ounces' | 'pounds' };
  dimensions: { length: number; width: number; height: number; units: 'inches' };
}): Promise<ShipBatchResponse> => {
  const response = await axios.post('/api/orders/ship-batch', payload);
  return validateResponse(ShipBatchResponseSchema, response.data, 'shipBatchOrders');
};

export const fetchShipLabelStatus = async (orderId: string): Promise<ShipLabelStatus> => {
  const response = await axios.get(`/api/orders/${encodeURIComponent(orderId)}/ship-label-status`);
  return validateResponse(ShipLabelStatusSchema, response.data, 'fetchShipLabelStatus');
};

export const reprintShipLabels = async (payload: {
  orderId?: string;
  pdfs?: ReadonlyArray<string>;
}): Promise<ReprintShipLabelResponse> => {
  const response = await axios.post('/api/orders/reprint-ship-label', payload);
  return validateResponse(ReprintShipLabelResponseSchema, response.data, 'reprintShipLabels');
};

export interface SplitOrderBoxLine {
  readonly sku: string;
  readonly quantity: number;
}

/** Box 1 stays on this ShipStation order; each later box becomes a new `<orderNumber>-<box>` order. */
export const splitOrderInShipStation = async (
  orderId: string | number,
  boxes: readonly (readonly SplitOrderBoxLine[])[],
): Promise<SplitOrderResponse> => {
  const response = await axios.post(`/api/orders/${encodeURIComponent(String(orderId))}/split`, { boxes });
  return validateResponse(SplitOrderResponseSchema, response.data, 'splitOrderInShipStation');
};

/** Print a fixture PDF through NAS print-pdf (same path as ship-batch labels). */
export const printTestShipLabel = async (): Promise<{ success: boolean; message?: string }> => {
  const response = await axios.post('/api/print-test-ship-label');
  return response.data as { success: boolean; message?: string };
};
